/**
 * Guía «Qué hacer en la consola de Twilio» (SPEC-estadisticas-conexion §A.7). Contenido estático,
 * en el orden en que Alan lo tiene que hacer.
 *
 * Cada paso puede apuntar a un paso del diagnóstico (`pasoDiagnostico`): cuando ése está en ok,
 * la guía lo marca como hecho. Así la guía no se queda diciendo «falta» sobre algo que ya anda.
 * Un paso cuyo ok no prueba nada sin otro antes (`requiere`) espera a ése también: «sin errores de
 * Meta» sin número registrado es sólo que no hubo envíos reales.
 *
 * Los enlaces van a la raíz de la consola: las rutas internas no están confirmadas y cambian.
 * Las fuentes (F…) son las páginas de Twilio de la §1 de la spec.
 */

import type { Diagnostico, IdPaso } from "../types";
import { CONSOLA_TWILIO, estadoDePaso } from "./conexion";

export interface PasoGuia {
  id: string;
  titulo: string;
  porQue: string;
  pasos: string[];
  enlace: string;
  fuente: string;
  opcional?: boolean;
  pasoDiagnostico?: IdPaso;
  /** Otro paso del diagnóstico que tiene que estar en ok para que el ok de `pasoDiagnostico` signifique algo. */
  requiere?: IdPaso;
}

export const GUIA_CONSOLA: readonly PasoGuia[] = [
  {
    id: "upgrade",
    titulo: "Pasar la cuenta a pago (Upgrade)",
    porQue: "Una cuenta Trial no acepta plantillas propias de WhatsApp ni deja registrar un número.",
    pasos: ["En la consola, arriba: «Upgrade». Cargá el medio de pago de Twilio."],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/usage/trials",
    pasoDiagnostico: "cuenta.tipo",
  },
  {
    id: "compliance",
    titulo: "Aprobar el Primary Compliance Profile",
    porQue: "Sin el perfil de cumplimiento aprobado, Twilio frena los envíos con el error 20003 (aunque el token sirva para leer la cuenta) o 21608.",
    pasos: ["En la consola: Trust Hub → Primary Customer Profile. Completalo con los datos de la empresa y esperá la aprobación."],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/api/errors/21608",
    pasoDiagnostico: "cumplimiento.compliance",
  },
  {
    id: "numero",
    titulo: "Registrar el número de WhatsApp (Self Sign-up)",
    porQue: "El número tiene que estar registrado en Meta a través de Twilio. Hasta que Meta aprueba el nombre visible, el tope es de 250 mensajes iniciados por la empresa cada 24 h.",
    pasos: [
      "Messaging → Senders → WhatsApp Senders → Create new sender.",
      "«Continue with Facebook» y seguí los pasos de Meta.",
      "Confirmá el número con el código que llega por SMS o por llamada.",
    ],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/whatsapp/self-sign-up",
    pasoDiagnostico: "numero.registrado",
  },
  {
    id: "meta",
    titulo: "Aceptar la invitación de Twilio en Meta y verificar el pago de WhatsApp",
    porQue: "Si no, Meta rechaza los envíos con el error 63020.",
    pasos: [
      "En Meta Business Manager: aceptá la invitación de Twilio.",
      "En la cuenta de WhatsApp Business: verificá el medio de pago.",
    ],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/api/errors/63020",
    pasoDiagnostico: "cumplimiento.meta",
    requiere: "numero.registrado",
  },
  {
    id: "servicio",
    titulo: "Messaging Service",
    porQue: "Es por donde salen los mensajes y por donde vuelven las respuestas.",
    pasos: ["No hace falta nada en la consola: el asistente lo elige o lo crea («Deenex · tu marca»)."],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/messaging/api/service-resource",
    pasoDiagnostico: "numero.servicio",
  },
  {
    id: "numero_en_servicio",
    titulo: "Número dentro del Messaging Service",
    porQue: "Si el número no está en el servicio, los envíos no salen por él.",
    pasos: ["Lo hace el asistente al tocar «Conectar» (o «Arreglarlo automáticamente» en el diagnóstico)."],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/messaging/api/messaging-service-channelsender-resource",
    pasoDiagnostico: "numero.en_servicio",
  },
  {
    id: "opt_out",
    titulo: "Baja automática (Advanced Opt-Out)",
    porQue: "Así quien responde BAJA deja de recibir mensajes aunque nuestro servidor no esté andando.",
    pasos: [
      "Messaging → Services → tu servicio → Opt-Out Management.",
      "Activá Advanced Opt-Out. Palabras de baja: BAJA, CANCELAR, PARAR y DETENER. De alta: ALTA. De ayuda: AYUDA.",
      "Las palabras en español son sugerencia nuestra: Twilio no las trae de fábrica.",
      "Después marcá «Ya lo hice» en el diagnóstico.",
    ],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/messaging/tutorials/advanced-opt-out",
    pasoDiagnostico: "cumplimiento.opt_out",
  },
  {
    id: "credenciales",
    titulo: "Copiar el Account SID y el Auth Token",
    porQue: "Con eso Deenex lee la cuenta y valida que los avisos vienen de Twilio.",
    pasos: [
      "Account → API keys & tokens: copiá el Account SID (empieza con AC) y el Auth Token.",
      "Pegalos en el paso 1 del asistente. No sirve una API Key estándar: hace falta el Auth Token.",
    ],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/api/errors/20003",
    pasoDiagnostico: "cuenta.credenciales",
  },
  {
    id: "sandbox",
    titulo: "(Opcional) Probar con el Sandbox antes del número propio",
    porQue: "Sirve para ver que el token, la URL pública y la firma andan. No sirve para campañas.",
    pasos: ["Abrí «Prueba con el Sandbox» más abajo en esta pantalla y seguí los pasos."],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/whatsapp/sandbox",
    opcional: true,
  },
  {
    id: "link_shortening",
    titulo: "(Opcional) Link Shortening",
    porQue: "Acorta los links y cuenta los clics. Cuesta USD 0,015 por mensaje, con los primeros 1.000 del mes sin cargo. En campañas con plantilla aprobada probablemente no mida nada.",
    pasos: [
      "Un dominio exclusivo (ej.: links.tumarca.com) con un CNAME a lsct.ashburn.us1.twilio.com.",
      "En la consola: el certificado y asociar el Messaging Service.",
      "Después prendelo en «Links acortados y clics», más abajo en esta pantalla.",
    ],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/messaging/features/link-shortening/onboarding-guide",
    opcional: true,
  },
  {
    id: "rotar_token",
    titulo: "Si rotás el Auth Token",
    porQue: "El token viejo deja de servir: los envíos y la validación de los avisos se cortan.",
    pasos: ["Pegá el nuevo en «Cambiar token», en el paso 1 del asistente."],
    enlace: CONSOLA_TWILIO,
    fuente: "https://www.twilio.com/docs/iam/api/authtoken",
    opcional: true,
  },
];

/**
 * La guía con cada paso marcado: `hecho` sólo si su paso del diagnóstico está en ok (y el que
 * `requiere`, también). Sin diagnóstico, o con el paso en `no_aplica` (simulado), no se marca nada:
 * «no aplica» no es «hecho».
 */
export function guiaConEstado(d: Diagnostico | null | undefined, guia: readonly PasoGuia[] = GUIA_CONSOLA): (PasoGuia & { hecho: boolean })[] {
  return guia.map((p) => ({
    ...p,
    hecho: !!p.pasoDiagnostico && estadoDePaso(d, p.pasoDiagnostico) === "ok" && (!p.requiere || estadoDePaso(d, p.requiere) === "ok"),
  }));
}
